/**
 * @file     SearchModeControl.tsx
 * @purpose  The Buy | Rent switch: a two-segment SegmentedControl preset shared
 *           by the home hero and the /properties results panel. The map view
 *           provides the live mode through SearchModeProvider; the control
 *           only reads and requests it, and fires search_mode_change.
 */
'use client';

import '@/core/i18n/deferred/rentalsSearch';
import { createContext, useContext, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { SegmentedControl } from '@/components/hub/SegmentedControl';
import { useListingEvents } from '@/hooks/useListingEvents';
import type { SearchMode } from './searchModeSwitch';

export type SearchModeSurface = 'home_hero' | 'properties';

type SearchModeContextValue = { mode: SearchMode; setMode: (mode: SearchMode) => void };

const SearchModeContext = createContext<SearchModeContextValue | null>(null);

/** Supplies the live search mode to controls rendered anywhere below (including server-rendered panel intros). */
export function SearchModeProvider({ mode, setMode, children }: SearchModeContextValue & { children: ReactNode }) {
  return <SearchModeContext.Provider value={{ mode, setMode }}>{children}</SearchModeContext.Provider>;
}

/** The live mode and its setter, or null outside a SearchModeProvider. */
export function useSearchMode(): SearchModeContextValue | null {
  return useContext(SearchModeContext);
}

export interface SearchModeControlProps {
  mode: SearchMode;
  onChange: (mode: SearchMode) => void;
  surface: SearchModeSurface;
  fullWidth?: boolean;
}

export function SearchModeControl({ mode, onChange, surface, fullWidth = false }: SearchModeControlProps) {
  const { t } = useTranslation();
  const track = useListingEvents();
  return (
    <SegmentedControl<SearchMode>
      ariaLabel={t('rentalsSearch.mode.label', { defaultValue: 'Search mode' })}
      value={mode}
      fullWidth={fullWidth}
      onChange={(next) => {
        if (next === mode) return;
        track('search_mode_change', { from: mode, to: next, surface });
        onChange(next);
      }}
      options={[
        { value: 'buy', label: t('rentalsSearch.mode.buy', { defaultValue: 'Buy' }) },
        { value: 'rent', label: t('rentalsSearch.mode.rent', { defaultValue: 'Rent' }) },
      ]}
    />
  );
}
